import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/contexts/AuthContext'
import { UserProvider, useUser } from '@/contexts/UserContext'
import { useAuth } from '@/contexts/AuthContext'
import { AppShell } from '@/components/layout'
import { HomePage } from '@/pages/HomePage'
import { EvaluationsPage } from '@/pages/EvaluationsPage'
import { EnhancementsPage } from '@/pages/EnhancementsPage'
import { AdminPage } from '@/pages/AdminPage'
import { AISummaryPage } from '@/pages/AISummaryPage'
import { AccessRequestForm } from '@/components/AccessRequestForm'
import { Loader2 } from 'lucide-react'

// Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

// Protected Route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading, login } = useAuth()
  const { hasAccess, isLoading: userLoading } = useUser()

  if (authLoading || userLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <p className="text-muted-foreground">Please sign in to continue</p>
        <button
          onClick={login}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Sign In
        </button>
      </div>
    )
  }

  if (!hasAccess) {
    return <AccessRequestForm />
  }

  return <>{children}</>
}

// Admin Route - only for Admin/SuperUser
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useUser()

  if (user && (user.role === 'Admin' || user.role === 'SuperUser')) {
    return <>{children}</>
  }

  return <Navigate to="/" replace />
}

// App Routes
function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<AppShell />}>
        <Route index element={<HomePage />} />
        <Route
          path="evaluate"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <EvaluationsPage />
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="enhance"
          element={
            <ProtectedRoute>
              <EnhancementsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="ai-summary"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AISummaryPage />
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="admin"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminPage />
              </AdminRoute>
            </ProtectedRoute>
          }
        />
      </Route>
    </Routes>
  )
}

// Main App
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </UserProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}
